const PENDING_KEY = "__mycontactlessmenu_stripe_pending__";
const PAYLOAD_KEY = "__mycontactlessmenu_payload__";

// A recovered order older than this must not reach the kitchen as fresh:
// same-meal window, tune if takeaway pre-orders need more.
const PENDING_TTL = 60 * 60 * 1000;

function successPath(type, id, paymentIntentId) {
    const page = (type === "t" || type === "d") ? "awaitsms" : "success";
    return `/${type}/q/${id}/orders/${page}/?provider=stripe&transactionid=${paymentIntentId}`;
}

// ponytail: localStorage throws on quota or in locked-down browsers, and the
// marker duplicates the order payload — a failed write must cost the recovery
// net, never the payment itself.
function write(key, value) {
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch (err) {
        console.error(err);
        return false;
    }
}

function markPending(clientSecret) {
    // Snapshot the order being paid: if the charge succeeds but the redirect
    // never happens, recovery must resend THIS order, not whatever cart the
    // customer may rebuild later.
    const payload = window.localStorage.getItem(PAYLOAD_KEY);
    write(PENDING_KEY, JSON.stringify({ clientSecret, payload, ts: Date.now() }));
}

function getPending() {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch (err) {
        return null;
    }
}

function clearPending() {
    window.localStorage.removeItem(PENDING_KEY);
}

async function retrieveOutcome(stripe, clientSecret) {
    const res = await stripe.retrievePaymentIntent(clientSecret);

    if (res && res.paymentIntent && res.paymentIntent.status === "succeeded") {
        return { outcome: "succeeded", paymentIntent: res.paymentIntent, recovered: true };
    }

    // Interrupted and not charged: a decline and a dropped request look the
    // same from here, so the customer gets the "could not verify" message.
    return { outcome: "unknown" };
}

// The confirmation request was interrupted (network drop, page freeze): the
// charge may have gone through anyway, so check the intent status.
async function afterInterrupted(stripe, clientSecret) {
    try {
        return await retrieveOutcome(stripe, clientSecret);
    } catch (err) {
        return { outcome: "unknown" };
    }
}

async function confirmCardPaymentSafe(stripe, clientSecret, params) {
    let res;
    try {
        res = await stripe.confirmCardPayment(clientSecret, params);
    } catch (err) {
        return afterInterrupted(stripe, clientSecret);
    }

    if (res.error) {
        // A retried confirmation on an already-charged intent comes back as an
        // error carrying the succeeded intent: the customer must not pay twice.
        if (res.error.payment_intent && res.error.payment_intent.status === "succeeded") {
            return { outcome: "succeeded", paymentIntent: res.error.payment_intent, recovered: true };
        }
        return { outcome: "failed", message: res.error.message };
    }

    return { outcome: "succeeded", paymentIntent: res.paymentIntent };
}

async function confirmWalletPayment(stripe, clientSecret, paymentMethodId, complete = () => { }) {
    let res;
    try {
        res = await stripe.confirmCardPayment(clientSecret, {
            payment_method: paymentMethodId,
        }, { handleActions: false });
    } catch (err) {
        complete("fail");
        return afterInterrupted(stripe, clientSecret);
    }

    if (res.error) {
        complete("fail");
        if (res.error.payment_intent && res.error.payment_intent.status === "succeeded") {
            return { outcome: "succeeded", paymentIntent: res.error.payment_intent, recovered: true };
        }
        return { outcome: "failed", message: res.error.message };
    }

    // The wallet sheet must be dismissed before any 3DS challenge can show.
    complete("success");

    if (res.paymentIntent.status === "requires_action") {
        return confirmCardPaymentSafe(stripe, clientSecret);
    }

    return { outcome: "succeeded", paymentIntent: res.paymentIntent };
}

async function checkPending(stripe, restaurantId) {
    const pending = getPending();
    if (!pending || !pending.clientSecret) {
        return { recovered: false, reason: "no_pending" };
    }

    if (!pending.ts || Date.now() - pending.ts > PENDING_TTL) {
        clearPending();
        // The intent id is the client secret prefix: enough to reconcile
        // manually from the Stripe dashboard, no network needed.
        return { recovered: false, reason: "expired", paymentIntentId: pending.clientSecret.split("_secret")[0] };
    }

    let res;
    try {
        res = await stripe.retrievePaymentIntent(pending.clientSecret);
    } catch (err) {
        return { recovered: false, reason: "check_failed" };
    }

    if (res && res.paymentIntent && res.paymentIntent.status === "succeeded") {
        // Sanity checks between the snapshot and the actual charge before
        // resending: a broken snapshot must never produce a paid order.
        let snapshot;
        try {
            snapshot = JSON.parse(pending.payload) || {};
        } catch (err) {
            snapshot = {};
        }

        // localStorage is shared across all restaurants on this domain: a
        // charge made at another restaurant must not fire here. Marker kept,
        // so recovery can still happen back at the right one (until the TTL).
        if (restaurantId !== null && restaurantId !== undefined && (!snapshot.restaurant || String(snapshot.restaurant.id) !== String(restaurantId))) {
            return { recovered: false, reason: "restaurant_mismatch", paymentIntentId: res.paymentIntent.id };
        }

        if (!snapshot.order || snapshot.order.amount !== res.paymentIntent.amount) {
            clearPending();
            return { recovered: false, reason: "amount_mismatch", paymentIntentId: res.paymentIntent.id };
        }

        // Restore the exact order the customer paid for (the cart may have
        // been rebuilt since). If the write fails the marker survives, so the
        // next mount tries again.
        if (!write(PAYLOAD_KEY, pending.payload)) {
            return { recovered: false, reason: "check_failed" };
        }
        clearPending();

        return { recovered: true, paymentIntentId: res.paymentIntent.id };
    }

    clearPending();
    return { recovered: false, reason: "not_paid" };
}

const stripeFlow = {
    successPath,
    markPending,
    getPending,
    clearPending,
    confirmCardPaymentSafe,
    confirmWalletPayment,
    checkPending,
};
export default stripeFlow;
